<script setup lang="ts">
import { computed } from 'vue'
import { RouterView } from 'vue-router'
import Box from '@uiKit/elements/Box.vue'
import { AuthTopHeader } from '@modules/auth'
import OnboardingTrustPanel from '@modules/onboarding/components/OnboardingTrustPanel.vue'
import { useOnboardingStore } from '@modules/onboarding/store/onboardingStore'

// Owns the onboarding chrome: top header, two-column shell, trust panel on
// the right. Mirrors `SignUpLayout` — the wizard container fills the left
// slot with the active step.
//
// `isSdkFullScreen` is the one wrinkle: the Sumsub SDK takes over the entire
// viewport and the wizard chrome must step out of the way. The layout reads
// the wizard store for this single flag; the rest of the wizard's state
// stays in the container, where the step components consume it.

const store = useOnboardingStore()
const isSdkFullScreen = computed(
  () => store.currentStepKey === 'kyc_onfido' && store.identitySubStep === 'sdk',
)
</script>

<template>
  <!-- Single RouterView — never remounts when isSdkFullScreen toggles.
       Splitting into two v-if/v-else branches with RouterView in each caused
       OnboardingWizardContainer to remount and reset identitySubStep to 'form'. -->
  <Box
    is="main"
    :class="['onboarding-shell', { 'onboarding-shell--sdk': isSdkFullScreen }]"
    flex
    block
  >
    <Box class="onboarding-shell__left" direction="col">
      <AuthTopHeader />
      <Box :class="isSdkFullScreen ? 'onboarding-shell__sdk-body' : 'onboarding-shell__body'">
        <RouterView />
      </Box>
    </Box>
    <Box v-if="!isSdkFullScreen" class="onboarding-shell__right" align="center" justify="center">
      <OnboardingTrustPanel />
    </Box>
  </Box>
</template>

<style scoped>
.onboarding-shell {
  height: 100dvh;
  overflow: hidden;
  background:
    radial-gradient(circle at 0% 0%, rgba(103, 85, 251, 0.06), transparent 35%),
    radial-gradient(circle at 100% 100%, rgba(103, 85, 251, 0.04), transparent 45%),
    var(--p-surface-50);
}

.onboarding-shell--sdk {
  background: var(--p-surface-50);
  overflow-y: auto;
}

.onboarding-shell__left {
  flex: 1 1 60%;
  min-width: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
}

.onboarding-shell__right {
  flex: 1 1 40%;
  min-width: 360px;
  padding: 2rem 1.5rem;
  display: flex;
}

.onboarding-shell__body {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2.5rem 2rem;
  display: flex;
  align-items: stretch;
  justify-content: center;
  box-sizing: border-box;
  overscroll-behavior: contain;
  scrollbar-width: none;
}

.onboarding-shell__body::-webkit-scrollbar {
  display: none;
}

.onboarding-shell--sdk .onboarding-shell__left {
  flex: 1 1 100%;
}

.onboarding-shell__sdk-body {
  width: 100%;
  padding: 1.5rem 2rem 2rem;
  box-sizing: border-box;
  display: flex;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
  align-items: flex-start;
  justify-content: center;
}

.onboarding-shell__sdk-body::-webkit-scrollbar {
  display: none;
}

@media (max-width: 992px) {
  .onboarding-shell {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
    overflow: visible;
  }

  .onboarding-shell__left {
    width: 100%;
  }

  .onboarding-shell__body {
    padding: 1.5rem 1rem;
    overflow: visible;
  }

  .onboarding-shell__right {
    display: none;
  }
}
</style>
